/**
 * /dashboard/creators — manage creator subscriptions.
 *
 * v6-styled wrapper around the existing /portfolio/subscriptions
 * data path (founder direction 2026-05-24: "apply the design you
 * added, just wire to make functional"). Pulls from the same APIs
 * the v1 page uses — listMySubscriptions / cancelSubscription /
 * subscribeToCreator — so the surface is live, not a mock.
 */
"use client";

import Link from "next/link";
import { useEffect, useState } from "react";
import { Heart, Star } from "lucide-react";
import {
  api,
  loadTokens,
  type SubscriptionRecord,
  type TipRecord,
} from "@/lib/api";
import { DashboardSubpageShell } from "@/components/v6/dashboard/DashboardSubpageShell";
import {
  mockSubscriptionRecords,
  mockTipsSent,
  mockTipsReceived,
} from "@/lib/v6/mockCreators";

export default function CreatorsPage() {
  const [subs, setSubs] = useState<SubscriptionRecord[] | null>(null);
  const [tipsSent, setTipsSent] = useState<TipRecord[]>([]);
  const [tipsReceived, setTipsReceived] = useState<TipRecord[]>([]);
  const [isDemo, setIsDemo] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Anonymous viewers see the demo dataset so the page isn't empty.
    if (!loadTokens()) {
      setSubs(mockSubscriptionRecords());
      setTipsSent(mockTipsSent());
      setTipsReceived(mockTipsReceived());
      setIsDemo(true);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const [rows, sentRows, recvRows] = await Promise.all([
          api.listMySubscriptions().catch(() => [] as SubscriptionRecord[]),
          api.listMyTipsSent().catch(() => [] as TipRecord[]),
          api.listMyTipsReceived().catch(() => [] as TipRecord[]),
        ]);
        if (cancelled) return;
        // If the user genuinely has nothing yet, show the simulated
        // dataset so the page renders with content — flagged as demo
        // so the UI can say so. Real rows always take precedence.
        if (rows.length === 0 && sentRows.length === 0 && recvRows.length === 0) {
          setSubs(mockSubscriptionRecords());
          setTipsSent(mockTipsSent());
          setTipsReceived(mockTipsReceived());
          setIsDemo(true);
        } else {
          setSubs(rows);
          setTipsSent(sentRows);
          setTipsReceived(recvRows);
          setIsDemo(false);
        }
      } catch (e: unknown) {
        if (!cancelled) {
          setSubs(mockSubscriptionRecords());
          setTipsSent(mockTipsSent());
          setTipsReceived(mockTipsReceived());
          setIsDemo(true);
          setError(
            e instanceof Error ? e.message : "Failed to load subscriptions",
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  async function handleCancel(sub: SubscriptionRecord) {
    setBusyId(sub.id);
    try {
      const next = await api.cancelSubscription(sub.id);
      setSubs((prev) =>
        (prev ?? []).map((s) =>
          s.id === sub.id
            ? { ...s, state: next.state, cancelledAt: next.cancelledAt }
            : s,
        ),
      );
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Cancel failed");
    } finally {
      setBusyId(null);
    }
  }

  async function handleResubscribe(sub: SubscriptionRecord) {
    setBusyId(sub.id);
    try {
      const res = await api.subscribeToCreator(sub.creatorUsername);
      setSubs((prev) =>
        (prev ?? []).map((s) => (s.id === sub.id ? { ...s, ...res } : s)),
      );
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Re-subscribe failed");
    } finally {
      setBusyId(null);
    }
  }

  const active = (subs ?? []).filter((s) => s.state === "active");
  const monthlyTotalCents = active.reduce(
    (sum, s) => sum + Number(s.monthlyPriceCents),
    0,
  );

  return (
    <DashboardSubpageShell
      title="Manage subscriptions"
      subtitle="Creators you follow + your paid tiers."
    >
      {isDemo && (
        <div
          className="mb-4 rounded-lg border px-3.5 py-2 text-[12px]"
          style={{
            borderColor: "var(--color-accent)",
            background:
              "color-mix(in srgb, var(--color-accent) 8%, transparent)",
            color: "var(--color-text2)",
          }}
        >
          <span style={{ color: "var(--color-accent)" }} className="font-semibold">
            Demo data
          </span>{" "}
          — your account doesn&apos;t have any subscriptions yet, so the
          page renders with simulated content. Real subscriptions take
          over the moment any are created.
        </div>
      )}

      <section
        className="mb-5 rounded-xl border p-5"
        style={{
          background: "var(--color-surface)",
          borderColor: "var(--color-border)",
        }}
      >
        <div className="flex flex-wrap items-baseline justify-between gap-3">
          <div>
            <div
              className="font-mono text-[10px] font-semibold uppercase tracking-[0.14em]"
              style={{ color: "var(--color-text3)" }}
            >
              Monthly subscriptions
            </div>
            <div className="mt-1 font-mono text-[28px] font-bold tabular-nums tracking-[-0.02em]">
              ${(monthlyTotalCents / 100).toFixed(2)}
            </div>
            <div
              className="mt-0.5 text-[12px]"
              style={{ color: "var(--color-text2)" }}
            >
              {active.length} active · {subs?.length ?? 0} total
            </div>
          </div>
          <Link
            href="/dashboard/transactions?filter=subscriptions"
            className="text-[12px] font-medium"
            style={{ color: "var(--color-accent)" }}
          >
            View subscription history →
          </Link>
        </div>
      </section>

      {error && (
        <div
          className="mb-3 rounded-md border px-3 py-2 text-[12px]"
          style={{
            borderColor: "var(--color-no)",
            background: "color-mix(in srgb, var(--color-no) 8%, transparent)",
            color: "var(--color-no)",
          }}
        >
          {error}
        </div>
      )}

      {subs === null ? (
        <SubsSkeleton />
      ) : subs.length === 0 ? (
        <div
          className="rounded-xl border px-5 py-10 text-center text-[14px]"
          style={{
            borderColor: "var(--color-border)",
            background: "var(--color-surface)",
            color: "var(--color-text2)",
          }}
        >
          You don&apos;t follow any creators yet.{" "}
          <Link
            href="/feed"
            className="font-semibold"
            style={{ color: "var(--color-accent)" }}
          >
            Find creators →
          </Link>
        </div>
      ) : (
        <ul
          style={{ listStyle: "none", margin: 0, padding: 0 }}
          className="space-y-2.5"
        >
          {subs.map((s) => (
            <CreatorRow
              key={s.id}
              sub={s}
              busy={busyId === s.id}
              onCancel={() => handleCancel(s)}
              onResubscribe={() => handleResubscribe(s)}
            />
          ))}
        </ul>
      )}

      {/* Tips section — sent + received in a 2-col split */}
      <div className="mt-8 grid grid-cols-1 gap-4 lg:grid-cols-2">
        <TipsCard label="Tips sent" rows={tipsSent} direction="out" />
        <TipsCard label="Tips received" rows={tipsReceived} direction="in" />
      </div>
    </DashboardSubpageShell>
  );
}

function TipsCard({
  label,
  rows,
  direction,
}: {
  label: string;
  rows: TipRecord[];
  direction: "in" | "out";
}) {
  const positive = direction === "in";
  return (
    <section
      className="rounded-xl border p-4"
      style={{
        background: "var(--color-surface)",
        borderColor: "var(--color-border)",
      }}
    >
      <div className="mb-3 flex items-center justify-between">
        <h2
          className="font-mono text-[11px] font-semibold uppercase tracking-[0.14em]"
          style={{ color: "var(--color-text3)" }}
        >
          {label}
        </h2>
        <span
          aria-hidden="true"
          className="flex h-7 w-7 items-center justify-center rounded-full"
          style={{
            background: positive
              ? "color-mix(in srgb, var(--color-yes) 14%, transparent)"
              : "color-mix(in srgb, var(--color-accent) 14%, transparent)",
            color: positive ? "var(--color-yes)" : "var(--color-accent)",
          }}
        >
          <Heart size={14} strokeWidth={2} />
        </span>
      </div>
      {rows.length === 0 ? (
        <div
          className="rounded-lg px-3 py-6 text-center text-[12px]"
          style={{ color: "var(--color-text3)" }}
        >
          No tips {direction === "in" ? "received" : "sent"} yet.
        </div>
      ) : (
        <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
          {rows.slice(0, 5).map((tip, idx) => (
            <li
              key={tip.id}
              className="flex items-center gap-3 py-2.5"
              style={{
                borderTop:
                  idx === 0 ? "none" : "1px solid var(--color-border)",
              }}
            >
              <div className="min-w-0 flex-1">
                <div className="truncate text-[13px] font-semibold">
                  @{tip.recipientUsername}
                </div>
                {tip.note && (
                  <div
                    className="mt-0.5 truncate text-[11px]"
                    style={{ color: "var(--color-text3)" }}
                  >
                    “{tip.note}”
                  </div>
                )}
                <div
                  className="mt-0.5 text-[10px]"
                  style={{ color: "var(--color-text3)" }}
                >
                  {formatDate(tip.createdAt)}
                </div>
              </div>
              <span
                className="flex-shrink-0 font-mono text-[14px] font-bold tabular-nums"
                style={{
                  color: positive ? "var(--color-yes)" : "var(--color-no)",
                }}
              >
                {positive ? "+" : "−"}$
                {(Number(tip.amountCents) / 100).toFixed(2)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function CreatorRow({
  sub,
  busy,
  onCancel,
  onResubscribe,
}: {
  sub: SubscriptionRecord;
  busy: boolean;
  onCancel: () => void;
  onResubscribe: () => void;
}) {
  const active = sub.state === "active";
  const monthly = (Number(sub.monthlyPriceCents) / 100).toFixed(2);
  return (
    <li
      className="flex flex-col gap-3 rounded-xl border p-4 sm:flex-row sm:items-center"
      style={{
        background: "var(--color-surface)",
        borderColor: "var(--color-border)",
      }}
    >
      <span
        aria-hidden="true"
        className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full text-[16px] font-bold"
        style={{
          background: "var(--color-surface2)",
          border: "1px solid var(--color-border)",
          color: "var(--color-text)",
        }}
      >
        {sub.creatorUsername[0]?.toUpperCase()}
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="truncate text-[14px] font-semibold">
            @{sub.creatorUsername}
          </span>
          <span
            className="rounded-full px-2 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-[0.1em]"
            style={{
              background: active
                ? "color-mix(in srgb, var(--color-accent) 14%, transparent)"
                : "var(--color-surface2)",
              color: active ? "var(--color-accent)" : "var(--color-text3)",
            }}
          >
            {active && <Star size={10} className="-mt-0.5 mr-0.5 inline" />}
            {sub.state}
          </span>
        </div>
        <div className="mt-1 text-[12px]" style={{ color: "var(--color-text2)" }}>
          ${monthly}/month
          {active && sub.currentPeriodExpiresAt && (
            <> · renews {formatDate(sub.currentPeriodExpiresAt)}</>
          )}
          {!active && sub.cancelledAt && (
            <> · cancelled {formatDate(sub.cancelledAt)}</>
          )}
        </div>
      </div>
      <div className="flex flex-shrink-0 gap-2">
        <Link
          href={`/u/${sub.creatorUsername}`}
          className="rounded-md border px-3 py-1.5 text-[12px] font-medium"
          style={{
            borderColor: "var(--color-border)",
            color: "var(--color-text2)",
          }}
        >
          View
        </Link>
        {active ? (
          <button
            type="button"
            disabled={busy}
            onClick={onCancel}
            className="rounded-md border px-3 py-1.5 text-[12px] font-medium disabled:opacity-50"
            style={{
              borderColor: "var(--color-border)",
              color: "var(--color-no)",
            }}
          >
            {busy ? "…" : "Cancel"}
          </button>
        ) : (
          <button
            type="button"
            disabled={busy}
            onClick={onResubscribe}
            className="rounded-md px-3 py-1.5 text-[12px] font-bold disabled:opacity-50"
            style={{
              background: "var(--color-accent)",
              color: "#0a0a0a",
            }}
          >
            {busy ? "…" : "Re-subscribe"}
          </button>
        )}
      </div>
    </li>
  );
}

function SubsSkeleton() {
  return (
    <ul
      style={{ listStyle: "none", margin: 0, padding: 0 }}
      className="space-y-2.5"
    >
      {[1, 2, 3].map((i) => (
        <li
          key={i}
          className="flex items-center gap-3 rounded-xl border p-4"
          style={{
            background: "var(--color-surface)",
            borderColor: "var(--color-border)",
          }}
        >
          <span
            className="h-12 w-12 flex-shrink-0 rounded-full"
            style={{ background: "var(--color-surface2)" }}
          />
          <div className="flex-1 space-y-2">
            <span
              className="block h-3 w-1/3 rounded"
              style={{ background: "var(--color-surface2)" }}
            />
            <span
              className="block h-2.5 w-1/2 rounded"
              style={{ background: "var(--color-surface2)" }}
            />
          </div>
        </li>
      ))}
    </ul>
  );
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}
